import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { initGA, trackPageView, GA_MEASUREMENT_ID, isGAEnabled, isGAReady } from '@/lib/analytics';

/**
 * AnalyticsProvider component
 * 
 * Responsibilities:
 * 1. Initialize GA4 on mount (if VITE_GA_MEASUREMENT_ID is configured)
 * 2. Track page views automatically on route changes
 * 
 * Must be placed inside RouterProvider to access useLocation.
 * 
 * @example
 * // In your App or layout component:
 * function App() {
 *   return (
 *     <>
 *       <AnalyticsProvider />
 *       <Outlet />
 *     </>
 *   );
 * }
 */
export function AnalyticsProvider() {
  const location = useLocation();
  const isInitialized = useRef(false);
  const previousPathname = useRef<string | null>(null);

  // Initialize GA4 once on mount
  useEffect(() => {
    if (isInitialized.current) return;
    if (!isGAEnabled()) {
      console.warn('[Analytics] GA4 not configured (VITE_GA_MEASUREMENT_ID missing)');
      return;
    }

    console.log('[Analytics] Loading gtag.js with ID:', GA_MEASUREMENT_ID);

    // Dynamically load the gtag.js script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    script.setAttribute('data-testid', 'gtag-script');
    document.head.appendChild(script);

    script.onload = () => {
      console.log('[Analytics] gtag.js loaded successfully');
      initGA();
      isInitialized.current = true;

      // Verify GA is ready before tracking
      setTimeout(() => {
        if (isGAReady()) {
          console.log('[Analytics] GA is ready to track events');
          // Track initial page view after init
          trackPageView(location.pathname + location.search);
          previousPathname.current = location.pathname;
        } else {
          console.error('[Analytics] GA failed to initialize properly');
        }
      }, 100);
    };

    script.onerror = () => {
      console.error('[Analytics] Failed to load gtag.js script from:', script.src);
    };

    return () => {
      // Cleanup script on unmount (rare, but for completeness)
      if (script.parentNode) {
        console.log('[Analytics] Cleaning up gtag.js script');
        script.parentNode.removeChild(script);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track page views on route changes
  useEffect(() => {
    // Skip if not initialized or same pathname (avoid double tracking)
    if (!isInitialized.current) return;
    if (previousPathname.current === location.pathname) return;

    if (!isGAReady()) {
      console.warn('[Analytics] GA not ready, skipping page_view:', location.pathname);
      return;
    }

    trackPageView(location.pathname + location.search);
    previousPathname.current = location.pathname;
  }, [location.pathname, location.search]);

  // This component doesn't render anything
  return null;
}
